import React from 'react';
import {
  atom,
  RecoilState,
  RecoilValueReadOnly,
  selector,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { SerializedStyles } from '@emotion/react';
import { icons } from '@assets/icons';
import { CrossIcon } from '@components/atoms';

export interface DialogProps {
  type?: 'plain' | 'success' | 'warning' | 'error' | 'hidden' | 'form';
  header?: string | React.JSX.Element;
  body?: string | React.JSX.Element;
  footer?: string | React.JSX.Element;
  styles?: SerializedStyles;
}

export const dialogContentsState = atom<DialogProps>({
  key: 'dialogContentsState',
  default: {
    type: 'hidden',
  },
});

export const noneChangePeriodState = atom<boolean>({
  key: 'noneChangePeriodState',
  default: true,
});

export const isVisibleDialogState = selector({
  key: 'visibleDialogState',
  get: ({ get }) => {
    const { type } = get(dialogContentsState);
    if (type === 'hidden') return false;
    return true;
  },
});

export const popupContentsState = atom<DialogProps>({
  key: 'popupContentsState',
  default: {
    type: 'hidden',
  },
});
export const isVisiblePopupState = selector({
  key: 'visiblePopupState',
  get: ({ get }) => {
    const { type } = get(popupContentsState);
    if (type === 'hidden') return false;
    return true;
  },
});

const Dialog = ({ type, header, body, footer, styles = css`` }: DialogProps) => {
  const btnActiveCase = useRecoilValue(noneChangePeriodState);
  const isVisible = useRecoilValue(type === 'form' ? isVisiblePopupState : isVisibleDialogState);
  const [dialogContent, setDialogContent] = useRecoilState(type === 'form' ? popupContentsState : dialogContentsState);

  return (
    <>
      {isVisible && (
        <DialogBackground>
          <DialogWrapper>
            {/*<DialogCloseButton*/}
            {/*  onClick={() => setDialogContent({ type: 'hidden' })}*/}
            {/*/>*/}
            <DialogHeader data-type={type || dialogContent.type}>
              <b>{header || dialogContent.header}</b>
            </DialogHeader>
            <DialogBody data-type={type || dialogContent.type}>
              <>{body || dialogContent.body} </>
            </DialogBody>
            <DialogFooter className={btnActiveCase ? 'btn-active' : ''}>{footer || dialogContent.footer}</DialogFooter>
          </DialogWrapper>
        </DialogBackground>
      )}
    </>
  );
};

export const FlexCenterStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const DialogTypeStyle = css`
  font-weight: bold;
  &[data-type='warning'] {
    b {
      color: #fbbb3a;
    }
  }
  &[data-type='error'] {
    b {
      color: #ff4c4c;
    }
  }
`;
export const DialogBackground = styled.div`
  position: fixed;
  left: 0;
  z-index: 9999;
  height: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;
export const DialogWrapper = styled.div`
  position: relative;
  width: 560px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
  border-radius: 10px;
  padding: 40px;

  & > .btn-active {
    & > .btn-active-target {
      background-color: #c8c8c8;
      cursor: not-allowed;
      border-color: #999999;
      color: #999999;
      &:hover {
        cursor: not-allowed;
        border-color: #999999;
        color: #999999;
      }
    }
  }
  ${(props: DialogProps) => props.styles}
`;

export const DialogCloseButton = styled.div`
  box-sizing: border-box;
  position: absolute;
  top: 3px;
  right: 3px;
  width: 30px;
  height: 30px;
  cursor: pointer;
  &:before {
    content: '';
    ${CrossIcon};
    transform: rotate(45deg);
    --color: #999999;
    width: 30px;
    height: 30px;
    border-radius: 100%;
  }
`;

export const DialogHeader = styled.section`
  ${FlexCenterStyle};
  ${DialogTypeStyle};
  flex-direction: column;
  line-height: 40px;
  font-size: 16px;
  font-weight: bold;
  &[data-type='success']:before {
    content: '';
    background: center / contain no-repeat url(${icons.success});
    width: 48px;
    height: 48px;
    color: #00c1ca;
  }
  &[data-type='warning']:before {
    content: '';
    background: center / contain no-repeat url(${icons.warning});
    width: 48px;
    height: 48px;
  }
  &[data-type='error']:before {
    content: '';
    background: center / contain no-repeat url(${icons.error});
    width: 48px;
    height: 48px;
  }
`;
export const DialogBody = styled.section`
  ${DialogTypeStyle};
  white-space: pre-line;
  font-size: 14px;
  text-align: center;
  & > p {
    margin: 0px;
  }
`;
export const DialogFooter = styled.section`
  ${FlexCenterStyle};
  flex-direction: row;
  margin-top: 20px;
  & > button {
    margin: auto 5px;
  }
`;
export default Dialog;
